import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { CenteredStoryText } from '@/components/microstory/text';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { UnderPressureCh1 } from '@/components/stories/under-pressure/chapter-1';
import { UnderPressureCh2 } from '@/components/stories/under-pressure/chapter-2';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/under-pressure.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as UnderPressureData }),
  getStoryProps('under-pressure', 'mental-burdens')
);

export default function UnderPressure(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const answers = [
    {
      id: 'more',
      label: t({ id: 'under-pressure.question.answer-1', message: 'More' }),
    },
    {
      id: 'less',
      label: t({ id: 'under-pressure.question.answer-2', message: 'Less' }),
    },
  ];

  const sharingImage = useSharingImage('under-pressure');

  const pageTitle = t({ message: 'Under pressure', id: 'under-pressure.title' });
  const pageDescription = t({
    id: 'under-pressure.description',
    message:
      'With each new generation, children face unique opportunities to thrive. But they can also feel burdened by expectations.',
  });

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.merlot} hasBackButton>
        <StoryHeader backgroundId={storySlug} css={{ backgroundPosition: 'left top' }}>
          <StoryTheme>
            <Trans id="under-pressure.theme">Mental Health</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="under-pressure.tagline">
              <Text type="heading">
                With each new generation, children face unique opportunities to thrive. But they can
                also feel <span>burdened by expectations.</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <CenteredStoryText>
          <Trans id="under-pressure.intro">
            We asked people about the pressure young people face today. What do you think?
          </Trans>
        </CenteredStoryText>

        <StoryQuestion
          question={t({
            message: `Do you think children today face more or less pressure from adults to succeed compared to when their parents were growing up?`,
            id: 'under-pressure.question.question',
          })}
          answers={answers}
          selectedAnswer={selectedAnswer}
          response={
            selectedAnswer?.id === 'more'
              ? t({
                  id: 'under-pressure.question.response-correct',
                  message: 'Young people tend to agree with you',
                })
              : t({
                  id: 'under-pressure.question.response-incorrect',
                  message: 'Young people tend to disagree',
                })
          }
          onAnswer={handleAnswer}
        />

        <UnderPressureCh1 data={data.pressure_median} />
        <UnderPressureCh2 data={data} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          relatedStoryId={relatedStoryId}
          sharing={{
            title: t({
              id: 'under-pressure.sharing.title',
              message: 'Under pressure',
            }),
            text: t({
              id: 'under-pressure.sharing.text',
              message:
                'With each new generation, children face unique opportunities to thrive. But they can also feel burdened by expectations.',
            }),
            url: sharingUrl,
          }}
        >
          <Trans id="under-pressure.wrap-up">
            What impact does <AnnotatedType>increased pressure</AnnotatedType> have on children
            today?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
